import React, { useEffect } from 'react';
import { Box, Text, Heading, Badge } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { Confirm, Globe } from '@wix/wix-ui-icons-common';
import { Card } from '@wix/gg-components';
import { StepContentWrapper } from 'components/StepContentWrapper';
import { useSelectedStoreName } from '../../stores/appStore';
import { useGTM } from '../../hooks/useGTM';
import styles from './SitePreviewContent.scss';
import { SitePreviewAction } from './SitePreviewAction';

const FEATURE_KEYS = [
  'site_preview.feature.fully_built_store',
  'site_preview.feature.products_added',
  'site_preview.feature.fast_suppliers',
  'site_preview.feature.customer_care',
] as const;

function renderFeatureItems({ t }: { t: (key: string) => string }) {
  return FEATURE_KEYS.map((key) => (
    <div key={key} className={styles.featureItem}>
      <Confirm className={styles.checkIcon} />
      <Text size="small" weight="bold">
        {t(key)}
      </Text>
    </div>
  ));
}

export function SitePreviewContent() {
  const { t } = useTranslation();
  const { trackPreviewPageView } = useGTM();
  const storeName = useSelectedStoreName();

  useEffect(() => {
    trackPreviewPageView(storeName || undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <StepContentWrapper>
      <Box direction="vertical">
        <div className={styles.contentCard}>
          <div className={styles.contentSection}>
            <Heading size="extraLarge" className={styles.storeReadyHeading}>
              {t('site_preview.store_ready_heading')}
            </Heading>

            <Text size="medium" secondary>
              {t('site_preview.store_ready_description')}
            </Text>
          </div>

          <div className={styles.readyBadge}>
            <div className={styles.badgeAccent} />
            <div className={styles.badgeContent}>
              <Box direction="horizontal" gap="8px" verticalAlign="middle">
                <Globe className={styles.badgeIcon} />

                <Text size="medium" weight="bold" className={styles.badgeText}>
                  {t('site_preview.badge.title')}
                </Text>
              </Box>

              <Text size="small" className={styles.timeEstimate}>
                {t('site_preview.badge.description')}
              </Text>
            </div>
          </div>

          <div className={styles.ctaWrapper}>
            <SitePreviewAction />
          </div>
        </div>

        <Card className={styles.promoCard}>
          <Badge skin="success" className={styles.promoBadge}>
            {t('site_preview.promo.badge')}
          </Badge>

          <Box direction="vertical" gap="24px" padding="24px">
            <Box direction="vertical" gap="8px">
              <Heading className={styles.promoHeading} size="medium">
                {t('site_preview.promo.heading')}
              </Heading>

              <Text className={styles.promoText} size="medium" secondary>
                {t('site_preview.promo.subtitle')}
              </Text>
            </Box>

            <Box direction="vertical" gap="16px">
              {renderFeatureItems({ t })}
            </Box>

            <div className={styles.ctaWrapper}>
              <SitePreviewAction text={t('site_preview.secondary.cta')} priority="secondary" />
            </div>
          </Box>
        </Card>
      </Box>
      <SitePreviewAction />
    </StepContentWrapper>
  );
}
